import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../../../styles/SubscriptionContainer.module.scss';
import Button from '../../../../../../components/Button';
import { gqlTypes } from '../../../../../../types';
import { usePurchaseSubscriptionContext } from '../../../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import AppPaths from '../../../../../../AppPaths';
import { useHistory } from 'react-router-dom';
import { inAppCancellation as events } from '../../../../../../lib/analytics/events';
import refreshIcon from '../../../../../../assets/images/icons/icon_refresh.svg';
import { DateTime } from 'luxon';
import RenewConfirmationModal from '../../../components/RenewConfirmationModal/RenewConfirmationModal';
import { useMutation } from '@apollo/client';
import {
  pauseMembershipMutation,
  purchaseSubscriptionDeviceQuery,
  renewSubscriptionMutation,
  resumeMembershipMutation,
} from '../../../../../../graphql-operations';
import { centsToDollarsNumber, getLoadingOrErrorElement } from '../../../../../../lib/util';
import { Image } from '../../../../../../components/Image';
import Pause from '../../../../../../assets/images/pause_white_bg.svg';
import CreditCard from '../../../../../../assets/images/credit-card.svg';
import NextArrow from '../../../../../../assets/images/next.svg';
import WarrantyIcon from '../../../../../../assets/images/icons/icon_warranty.svg';
import MessageIcon from '../../../../../../assets/images/icons/icon_message.svg';
import PhoneIcon from '../../../../../../assets/images/icons/icon_phone.svg';
import { SubscriptionCategory } from '../../../util/types';
import { ConfirmModal } from '../../../../../../components/modals';
import Loading from '../../../../../../components/Loading';
import { onValidAppVersion, maybeTruncatePetName } from '../../../util/helpers';
import { Group, Paper, Stack, Text } from '@mantine/core';
import { useSelector } from 'react-redux';
import * as types from '../../../../../../types';

interface ManageSubscriptionButtonsProps {
  canRenew: boolean;
  subscriptionCategory: SubscriptionCategory;
}

export default function ManageSubscriptionButtons({ canRenew, subscriptionCategory }: ManageSubscriptionButtonsProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const { device } = usePurchaseSubscriptionContext();
  const [error, setError] = useState('');
  const userEmail = useSelector((state: types.AppState) => state.session)?.email;
  const billingEmail = device.subscription?.billingEmail;
  const paramsString = window.location.search;
  const searchParams = new URLSearchParams(paramsString);
  const resumeClicked = searchParams.get('resumeClicked') === 'true';
  const activePause = DateTime.now() >= DateTime.fromISO(device.subscription?.pausedAt);
  const petName = device.pet?.name ?? t('your_dog');
  const shortPetName = maybeTruncatePetName(petName);
  const isMonthlyPlan = subscriptionCategory === SubscriptionCategory.MONTHLY;
  const isOlderModel = device.currentCollarModel === 'S1' || device.currentCollarModel === 'S2';
  const [canPause, setCanPause] = useState(
    !device.subscription?.isPaused &&
      !!device.shouldOfferPauseMonthDuringCancellation &&
      onValidAppVersion(device, window.location.search),
  );
  const [canResume, setCanResume] = useState(isMonthlyPlan && !!device.subscription?.isPaused);

  const [renewMutation, renewSubscriptionMutationState] = useMutation<
    gqlTypes.ECOMMERCE_renewSubscription,
    gqlTypes.ECOMMERCE_renewSubscriptionVariables
  >(renewSubscriptionMutation, {
    onCompleted: (data) => {
      const success = !!data.renewSubscription?.success;
      if (!success) {
        throw new Error('Failed to renew subscription: contact customer support.');
      }
      history.push({
        pathname: AppPaths.Subscription.RenewConfirmation(device.moduleId),
        search: window.location.search,
      });
    },
  });

  const [pauseMutation, pauseMutationState] = useMutation<
    gqlTypes.ECOMMERCE_pauseMembership,
    gqlTypes.ECOMMERCE_pauseMembershipVariables
  >(pauseMembershipMutation, {
    onCompleted: (data) => {
      const success = !!data.pauseMembership?.success;
      if (!success) {
        setError(
          data.pauseMembership?.reasonForFailure
            ? t('error_reason', { reason: data.pauseMembership?.reasonForFailure })
            : t('failed_to_pause_membership_contact_customer_support'),
        );
      } else {
        history.push({
          pathname: AppPaths.Subscription.PauseMembershipConfirmed(device.moduleId),
          search: window.location.search,
          state: { subscriptionCategory },
        });
      }
    },
  });

  const [resumeMutation, resumeMutationState] = useMutation<
    gqlTypes.ECOMMERCE_resumeMembership,
    gqlTypes.ECOMMERCE_resumeMembershipVariables
  >(resumeMembershipMutation, {
    refetchQueries: [{ query: purchaseSubscriptionDeviceQuery }],
    onCompleted: (data) => {
      const success = !!data.resumeMembership?.success;
      if (!success) {
        setError(
          data.resumeMembership?.reasonForFailure
            ? t('error_reason', { reason: data.resumeMembership?.reasonForFailure })
            : t('failed_to_resume_membership_contact_customer_support'),
        );
      } else {
        setCanResume(false);
        setCanPause(true);
        history.push({
          pathname: AppPaths.Subscription.ResumeMembershipConfirmed(device.moduleId),
          search: window.location.search,
        });
      }
    },
  });

  if (pauseMutationState.loading || resumeMutationState.loading) {
    return <Loading />;
  }

  const hasSubscription = device.subscriptionAccessType === gqlTypes.SubscriptionAccessType.FULL_ACCESS;
  if (!hasSubscription) {
    return null;
  }

  const loadingOrErrorElement = getLoadingOrErrorElement(
    renewSubscriptionMutationState.loading,
    renewSubscriptionMutationState.error,
  );
  if (loadingOrErrorElement) {
    return loadingOrErrorElement;
  }

  const handleRenew = () => {
    events.renewMembership({ petId: device.pet?.id });
    renewMutation({
      variables: {
        input: {
          moduleId: device.moduleId,
        },
      },
    });
  };

  const handlePause = () => {
    if (canPause) {
      const NUM_TERMS_TO_PAUSE = 1;
      events.claimPauseMembership({ petId: device.pet!.id, subscriptionCategory });
      pauseMutation({
        variables: {
          input: {
            recurlySubscriptionId: device.subscription!.id,
            moduleId: device.moduleId,
            numTerms: NUM_TERMS_TO_PAUSE,
          },
        },
      });
    } else {
      const errMessage = billingEmail
        ? t('this_membership_is_associated_with_billing_email_to_manage_this_membership_please_login_to_that_account', {
            billingEmail,
          })
        : t('unable_to_find_billing_email_for_this_device_please_contact_customer_support');
      setError(errMessage);
    }
  };

  const handleResume = () => {
    if (canResume) {
      events.resumeMembership({ petId: device.pet!.id, subscriptionCategory });
      resumeMutation({
        variables: {
          input: {
            recurlySubscriptionId: device.subscription!.id,
            moduleId: device.moduleId,
          },
        },
      });
    } else {
      const errMessage = billingEmail
        ? t('this_membership_is_associated_with_billing_email_to_manage_this_membership_please_login_to_that_account', {
            billingEmail,
          })
        : t('unable_to_find_billing_email_for_this_device_please_contact_customer_support');
      setError(errMessage);
    }
  };

  function renewButton() {
    return (
      <RenewConfirmationModal
        onContinue={() => handleRenew()}
        pageName={t('membership')}
        petId={device.pet!.id}
        trigger={
          <Button className={styles.button} secondary>
            <Image image={{ type: 'image', url: refreshIcon }} />
            <div className={styles.textContainer}>
              <p>{t('renew_membership')}</p>
            </div>
            <Image image={{ type: 'image', url: NextArrow }} />
          </Button>
        }
      />
    );
  }

  function manageMembershipText() {
    return (
      <Stack spacing={32} pb={20}>
        <Text color="type-light-secondary" fz={16} fw={500}>
          {t('to_manage_your_membership_please_visit_memberships_tryfi_com_manage')}
        </Text>
        <Paper bg="background-light-secondary" p={20} radius="lg">
          <Stack spacing={16}>
            <Text fz={14} fw={600}>
              {t('your_membership_includes')}
            </Text>
            {!isOlderModel ? (
              <Group noWrap>
                <Image image={{ type: 'image', url: WarrantyIcon }} />
                <Text fz={14}>
                  <Text span inherit fw={600}>
                    {t('fi_warranty')}
                  </Text>{' '}
                  {t('extended_hardware_warranty_for_active_memberships')}
                </Text>
              </Group>
            ) : (
              <></>
            )}
            <Group noWrap>
              <Image image={{ type: 'image', url: MessageIcon }} />
              <Text fz={14}>
                <Text span inherit fw={600}>
                  {t('online_support')}
                </Text>{' '}
                {t('7_days_week_on_chat_or_email_to_answer_any_questions')}
              </Text>
            </Group>
            <Group noWrap>
              <Image image={{ type: 'image', url: PhoneIcon }} />
              <Text fz={14}>
                <Text span inherit fw={600}>
                  {t('phone_support')}
                </Text>{' '}
                {t('talk_to_a_human_7_days_a_week_10am_8pm_et')}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  function pauseButton() {
    return (
      <ConfirmModal
        trigger={
          <Button className={styles.button} secondary>
            <Image image={{ type: 'image', url: Pause }} />
            <div className={styles.textContainer}>
              <p>{t('pause_membership')}</p>
            </div>
            <Image image={{ type: 'image', url: NextArrow }} />
          </Button>
        }
        body={
          <>
            <h2>{t('are_you_sure')}</h2>
            <p>
              {t(
                'this_will_pause_your_access_to_the_fi_app_gps_tracking_and_pet_name_data_on_date_and_skip_billing_for_1_month',
                {
                  petName: device.pet?.name,
                  date: DateTime.fromISO(device.subscription?.currentTermEndsAt).toFormat('L/d/yy'),
                },
              )}
            </p>
          </>
        }
        acceptText={t('confirm')}
        declineText={t('no_go_back')}
        onAccept={handlePause}
      />
    );
  }

  function resumeButton() {
    return (
      <ConfirmModal
        trigger={
          <Button className={styles.button} secondary>
            <Image image={{ type: 'image', url: refreshIcon }} />
            <div className={styles.textContainer}>
              <p>{t('resume_membership')}</p>
            </div>
            <Image image={{ type: 'image', url: NextArrow }} />
          </Button>
        }
        body={
          <>
            <h2>{t('resume_membership')}</h2>
            {activePause ? (
              <p>
                {t('your_access_to_the_fi_app_will_automatically_resume_and_you_will_be_billed_amount', {
                  amount: device.subscription?.subscriptionOption?.priceInCents
                    ? `$${centsToDollarsNumber(device.subscription?.subscriptionOption?.priceInCents)}`
                    : t('today'),
                })}
              </p>
            ) : (
              <p>
                {t('the_scheduled_pause_on_pet_name_membership_will_be_canceled_you_wont_be_billed', {
                  petName: shortPetName,
                })}
              </p>
            )}
          </>
        }
        acceptText={t('confirm')}
        declineText={t('no_go_back')}
        onAccept={handleResume}
        open={resumeClicked}
      />
    );
  }

  function managePaymentButton() {
    if (!userEmail || !billingEmail || userEmail !== billingEmail) {
      return null;
    }

    return (
      <Button
        className={styles.button}
        secondary
        onClick={() => {
          events.updatePaymentMethodClicked({ petId: device.pet?.id });
          history.push({
            pathname: AppPaths.Subscription.UpdatePayment(device.moduleId),
            search: window.location.search,
          });
        }}
      >
        <Image image={{ type: 'image', url: CreditCard }} />
        <div className={styles.textContainer}>
          <p>{t('manage_payment_information')}</p>
        </div>
        <Image image={{ type: 'image', url: NextArrow }} />
      </Button>
    );
  }

  const buttonsToSurface = () => {
    if (canRenew) {
      events.subscriptionSplashButtonsLoaded({ petId: device.pet!.id }, 'Renew, Manage Payment');
      return (
        <>
          {renewButton()}
          {managePaymentButton()}
        </>
      );
    }

    if (canResume) {
      events.subscriptionSplashButtonsLoaded({ petId: device.pet!.id }, 'Resume, Manage Payment');
      return (
        <>
          {resumeButton()}
          {managePaymentButton()}
        </>
      );
    }

    if (canPause) {
      events.subscriptionSplashButtonsLoaded({ petId: device.pet!.id }, 'Manage Link, Pause, Manage Payment');
      return (
        <>
          {manageMembershipText()}
          {pauseButton()}
          {managePaymentButton()}
        </>
      );
    }

    events.subscriptionSplashButtonsLoaded({ petId: device.pet!.id }, 'Manage Link, Manage Payment');
    return (
      <>
        {manageMembershipText()}
        {managePaymentButton()}
      </>
    );
  };

  return (
    <>
      <div className={styles.membershipOptionsContainer}>
        {error === '' ? (
          <>
            {buttonsToSurface()}
            {canRenew && (
              <div className={styles.renewCopy}>
                <div>
                  <strong>{t('changed_your_mind')}</strong>
                  <br />
                  {t('renew_to_avoid_loss_of_app_access_and_keep_your_membership_active_past_date', {
                    date: DateTime.fromISO(device.subscription?.expiresAt).toFormat('MMMM d, y'),
                  })}
                </div>
              </div>
            )}
            {canResume && (
              <div className={styles.resumeCopy}>
                <div>
                  <strong>{t('changed_your_mind')}</strong>
                  <br />
                  {t('resume_to_avoid_loss_of_app_access_and_keep_your_membership_active_past_date', {
                    date: DateTime.fromISO(device.subscription?.currentTermEndsAt).toFormat('MMMM d, y'),
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={styles.error}>{error}</div>
        )}
      </div>
    </>
  );
}
